import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Components } from 'react-markdown';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
  const components: Components = {
    // Headers
    h1: ({ children }) => (
      <h1 className="text-3xl font-bold text-critique-primary mb-4 mt-6 font-display">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-2xl font-bold text-critique-primary mb-3 mt-5 font-display">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl font-semibold text-critique-accent mb-2 mt-4">
        {children}
      </h3>
    ),
    
    // Paragraphs
    p: ({ children }) => (
      <p className="mb-4 leading-relaxed text-neutral-lighter">
        {children}
      </p>
    ),
    
    // Lists
    ul: ({ children }) => (
      <ul className="list-disc list-inside mb-4 space-y-2 text-neutral-lighter">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-inside mb-4 space-y-2 text-neutral-lighter">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="ml-4">
        {children}
      </li>
    ),
    
    // Emphasis
    strong: ({ children }) => (
      <strong className="font-bold text-critique-accent">
        {children}
      </strong>
    ),
    em: ({ children }) => (
      <em className="italic text-critique-secondary">
        {children}
      </em>
    ),
    
    // Code
    code: ({ children }) => (
      <code className="bg-neutral-darker px-2 py-1 rounded text-sm font-mono text-orange-400">
        {children}
      </code>
    ),
    pre: ({ children }) => (
      <pre className="bg-neutral-darker p-4 rounded-lg mb-4 overflow-x-auto">
        {children}
      </pre>
    ),
    
    // Blockquotes (for callouts)
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-critique-primary bg-red-900/10 pl-4 py-2 mb-4 italic">
        {children}
      </blockquote>
    ),
    
    // Tables
    table: ({ children }) => (
      <div className="overflow-x-auto mb-4">
        <table className="min-w-full border-collapse">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="border-b-2 border-critique-primary">
        {children}
      </thead>
    ),
    tbody: ({ children }) => (
      <tbody className="divide-y divide-neutral-light">
        {children}
      </tbody>
    ),
    tr: ({ children }) => (
      <tr className="hover:bg-neutral-gray/30 transition-colors">
        {children}
      </tr>
    ),
    th: ({ children }) => (
      <th className="px-4 py-2 text-left font-semibold text-critique-primary">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-4 py-2 text-neutral-lighter">
        {children}
      </td>
    ),
    
    // Horizontal rule
    hr: () => (
      <hr className="border-t border-neutral-light my-6" />
    ),
    
    // Links
    a: ({ href, children }) => (
      <a 
        href={href} 
        className="text-critique-primary hover:text-critique-accent underline transition-colors"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
  };

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;